import React from "react";
import { getPublisherAvatarUrl } from "../../utils";
import {
  formatPostDate,
  getPlainExcerpt,
  getPostTypeLabel,
} from "./memberUtils";
import {
  FeaturedCardBody,
  FeaturedCardGrid,
  FeaturedCardMedia,
  FeaturedCardMediaGradient,
  FeaturedCardRoot,
  FeaturedContributorBadge,
  GridCardInner,
  GridCardMedia,
  GridCardMediaGradient,
  PostAuthorLink,
  PostAuthorRow,
  PostCardOverlayLink,
  PostCardRoot,
  PostExcerpt,
  PostMeta,
  PostMetaDate,
  PostTag,
  PostTags,
  PostTitle,
  PostTitleFeatured,
  PostTypeBadge,
  PostTypePill,
} from "./Members.styles";

const typeIcon = (typeLabel) =>
  typeLabel === "Video" ? "play_circle" : "menu_book";

const PostTagsList = ({ tags, variant = "hash" }) => {
  if (!tags?.length) return null;

  return (
    <PostTags>
      {tags.slice(0, 4).map((tag) => (
        <PostTag
          key={tag.id || tag.name}
          $outline={variant === "outline"}
          $hash={variant === "hash"}
        >
          {variant === "hash" ? `#${tag.name}` : tag.name}
        </PostTag>
      ))}
    </PostTags>
  );
};

const PostMetaRow = ({ post, bordered = false }) => (
  <PostMeta $bordered={bordered}>
    <PostMetaDate>
      <span className="material-icons" aria-hidden="true">
        event
      </span>
      {formatPostDate(post.publishedAt)}
    </PostMetaDate>
  </PostMeta>
);

const AuthorBlock = ({ post }) => {
  const author = post.author || {};
  const isFeaturedContributor = Boolean(author.isFeaturedContributor);

  return (
    <PostAuthorLink
      to={`/analyst/${author.slug}`}
      onClick={(event) => event.stopPropagation()}
    >
      <img src={getPublisherAvatarUrl(author)} alt="" loading="lazy" />
      <div>
        <strong>{author.name}</strong>
        {author.title && <span>{author.title}</span>}
        {isFeaturedContributor && (
          <FeaturedContributorBadge>Featured Contributor</FeaturedContributorBadge>
        )}
      </div>
    </PostAuthorLink>
  );
};

/** Large hero layout for the first post on page 1 — visual only, not a "Featured" flag. */
export const LeadMemberPostCard = ({ post }) => {
  const typeLabel = getPostTypeLabel(post);
  const imageUrl = post.image?.original || post.image?.small;

  return (
    <FeaturedCardRoot>
      <FeaturedCardGrid>
        <FeaturedCardMedia>
          {imageUrl && <img src={imageUrl} alt="" loading="lazy" />}
          <FeaturedCardMediaGradient aria-hidden="true" />
          <PostTypePill>
            <span className="material-icons" aria-hidden="true">
              {typeIcon(typeLabel)}
            </span>
            {typeLabel}
          </PostTypePill>
        </FeaturedCardMedia>

        <FeaturedCardBody>
          <AuthorBlock post={post} />
          <PostTitleFeatured $featured>{post.title}</PostTitleFeatured>
          <PostExcerpt>{getPlainExcerpt(post.excerpt)}</PostExcerpt>
          <PostTagsList tags={post.tags} variant="outline" />
          <PostMetaRow post={post} />
        </FeaturedCardBody>
      </FeaturedCardGrid>

      <PostCardOverlayLink to={post.url}>
        <span>Read {post.title}</span>
      </PostCardOverlayLink>
    </FeaturedCardRoot>
  );
};

export const MemberPostCard = ({ post }) => {
  const typeLabel = getPostTypeLabel(post);
  const imageUrl = post.image?.small || post.image?.original;

  return (
    <PostCardRoot>
      <GridCardInner>
        <GridCardMedia>
          {imageUrl && <img src={imageUrl} alt="" loading="lazy" />}
          <GridCardMediaGradient aria-hidden="true" />
        </GridCardMedia>

        <PostAuthorRow>
          <AuthorBlock post={post} />
          <PostTypeBadge>
            <span className="material-icons" aria-hidden="true">
              {typeIcon(typeLabel)}
            </span>
            {typeLabel}
          </PostTypeBadge>
        </PostAuthorRow>

        <PostTitle as="h3">{post.title}</PostTitle>
        <PostExcerpt $compact>{getPlainExcerpt(post.excerpt)}</PostExcerpt>
        <PostTagsList tags={post.tags} variant="hash" />
        <PostMetaRow post={post} bordered />
      </GridCardInner>

      <PostCardOverlayLink to={post.url}>
        <span>Read {post.title}</span>
      </PostCardOverlayLink>
    </PostCardRoot>
  );
};
